import React, { useEffect, useState } from "react";
import api from "@/utils/api";
import { ChevronDown, Lock, Palette } from "lucide-react";
import { Input } from "@/components/ui/input";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export default function InvoiceDesignOverride({ value, onChange, locked, defaultDesign, defaultAccent }) {
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(!!(value?.design || value?.accent_color));
  const ov = value || {};

  useEffect(() => {
    api.get("/invoices/designs").then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, []);

  if (!meta || meta.is_lex || !meta.can_choose) return null;

  const effectiveKey = ov.design || defaultDesign || "klar";
  const effectiveName = meta.designs.find((d) => d.key === effectiveKey)?.name || "Klar";
  const accent = ov.accent_color || defaultAccent || "#1e40af";
  const set = (patch) => {
    const next = { ...ov, ...patch };
    if (!next.design) delete next.design;
    if (!next.accent_color) delete next.accent_color;
    onChange(Object.keys(next).length ? next : null);
  };

  return (
    <div className="bg-gray-50 border rounded-xl" data-testid="invoice-design-override">
      <button type="button" className="w-full flex items-center justify-between px-4 py-3 text-left" onClick={() => setOpen((o) => !o)} data-testid="invoice-design-override-toggle">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Palette className="h-4 w-4 text-slate-500" /> Design
          <span className="text-xs font-normal text-slate-500">
            {ov.design || ov.accent_color ? `Abweichend: ${effectiveName}` : `Wie in Einstellungen (${effectiveName})`}
          </span>
          {locked && <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] text-slate-600"><Lock className="h-3 w-3" /> gesperrt</span>}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={locked} onClick={() => set({ design: undefined })} data-testid="design-override-default"
              className={`rounded-full border px-3 py-1 text-xs ${!ov.design ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white"} ${locked ? "opacity-60" : ""}`}>
              Standard
            </button>
            {meta.designs.map((d) => (
              <button key={d.key} type="button" disabled={locked} onClick={() => set({ design: d.key })} data-testid={`design-override-${d.key}`}
                className={`rounded-full border px-3 py-1 text-xs ${ov.design === d.key ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white"} ${locked ? "opacity-60" : ""}`}>
                {d.name}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">Akzentfarbe</span>
            {meta.accent_presets.map((c) => (
              <button key={c} type="button" disabled={locked} aria-label={`Farbe ${c}`} onClick={() => set({ accent_color: c })} data-testid={`design-override-accent-${c.slice(1)}`}
                className={`h-6 w-6 rounded-full border-2 ${accent.toLowerCase() === c ? "border-slate-900" : "border-white shadow"} ${locked ? "opacity-60" : ""}`} style={{ backgroundColor: c }} />
            ))}
            <Input value={ov.accent_color || ""} disabled={locked} placeholder={defaultAccent || "#1e40af"} data-testid="design-override-hex"
              onChange={(e) => set({ accent_color: e.target.value || undefined })}
              className={`h-7 w-24 font-mono text-xs ${ov.accent_color && !HEX_RE.test(ov.accent_color) ? "border-red-400" : ""}`} />
            {ov.accent_color && <button type="button" disabled={locked} className="text-xs text-slate-500 underline" onClick={() => set({ accent_color: undefined })}>Farbe zurücksetzen</button>}
          </div>
          <p className="text-xs text-slate-500">Gilt nur für diese Rechnung. Sobald die Rechnung versendet oder bezahlt ist, ist das Design – wie der übrige Inhalt – gesperrt, damit das PDF unverändert reproduzierbar bleibt.</p>
        </div>
      )}
    </div>
  );
}
